import React, { useState } from "react";
import PropTypes from "prop-types";
import { Accordion, AccordionSummary, Box, Grid, Button } from "@mui/material";
import styles from "./DealerShipDetails.module.scss";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import EditIcon from "../../../shared/Edit/EditIcon";
import { withTranslation } from "react-i18next";
import DealerEmployeeTable from "./DealerEmployeeTable";
import HasPermissionTo from "../../../common/HasPermissionTo";
import DealerDetailsClient from "../../../clients/DealerDetailsClient";
import crStyles from "../../customerHandling/crStyles";
import UpperCaseText from "../../../utils/UpperCaseText";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import { logMsg } from "../../../common/Logger";

const DealerContactInfo = (props) => {
  const { dealers, user, updateDealerPhoneAndEmail, isMainViewingSDLR, t } =
    props;

  const [expand, setExpand] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentEmailText, setCurrentEmailText] = useState(
    dealers.emailAddress
  );
  const [currentPhoneText, setCurrentPhoneText] = useState(
    dealers.phoneNumber
  );
  const [emailText, setEmailText] = useState(dealers.emailAddress);
  const [phoneText, setPhoneText] = useState(dealers.phoneNumber);

  const rolePermissions = user?.permissions?.rolePermissions || {};

  const showAddress = Object.hasOwn(rolePermissions, "businessAddress:view");
  const addressGridClassName = showAddress ? "" : styles.addressFlex;
  const isViewingMainSDLR = isMainViewingSDLR(dealers);

  const flipIcon = () => setExpand((prev) => !prev);

  const cancelEdit = () => {
    setEditMode(false);
    setEmailText(currentEmailText);
    setPhoneText(currentPhoneText);
  };

  // The inputs are controlled (value={emailText}, value={phoneText}), so
  // we can read the latest user-entered value from state instead of
  // querying the DOM by id. This avoids brittle id coupling and is also
  // easier to unit test.
  const updatePhone = (completeDealerId, emailIdValue) => {
    DealerDetailsClient.updateDealerPhoneNumber(
      user,
      completeDealerId,
      phoneText
    )
      .then((dealer) => {
        setCurrentPhoneText(phoneText);
        setEditMode(false);
        updateDealerPhoneAndEmail(dealer, emailIdValue, phoneText);
      })
      .catch((error) => {
        logMsg("Failed to update dealer phone", error);
      });
  };

  const updateEmailAndPhone = (completeDealerId) => {
    DealerDetailsClient.updateDealerEmailAddress(
      user,
      completeDealerId,
      emailText
    )
      .then(() => {
        setCurrentEmailText(emailText);
        updatePhone(completeDealerId, emailText);
      })
      .catch((error) => {
        logMsg("Failed to update dealer email", error);
      });
  };

  const editDealershipInfo = () => setEditMode(true);

  const emailOnChange = (event) => setEmailText(event.target.value);

  const phoneOnChange = (event) => setPhoneText(event.target.value);

  return (
    <div className={styles.divPadding}>
      <Accordion
        classes={{ root: styles.expPanelNoBoxShadow }}
        style={{ marginBottom: "10px" }}
        defaultExpanded={true}
        onChange={flipIcon}
      >
        <AccordionSummary style={crStyles.headerBarBg} id="exp">
          <div className={styles.headerBar}>
            <div>
              {dealers.name}
              <span>({dealers.regionalId})</span>
            </div>
            {expand ? (
              <ExpandLessIcon id="less" />
            ) : (
              <ExpandMoreIcon id="more" />
            )}
          </div>
          <br />
        </AccordionSummary>
        <div className={styles.businessDetailsContainer}>
          <div className={styles.sectionHeaderDealership}>
            {t("DealerShipDetails.dealershipHeader")}
          </div>
          <div className={styles.subHeader}>
            <HasPermissionTo
              perform={["businessAddress:view"]}
              permissions={rolePermissions}
              render={() => (
                <span
                  id="businessDetails"
                  className={styles.businessDetailsInputGridModel}
                >
                  {t("DealerShipDetails.businessDetails")}
                </span>
              )}
            />
          </div>
          <ValidatorForm
            style={{ width: "100%" }}
            onSubmit={() => updateEmailAndPhone(dealers.completeDealerId)}
          >
            <Grid container className={addressGridClassName}>
              <HasPermissionTo
                perform={["businessAddress:view"]}
                permissions={rolePermissions}
                render={() =>
                  user.market.toUpperCase() === "DEU" ? (
                    <Grid item md={3} className={styles.divAddress}>
                      <span>{dealers.address.street} </span>
                      <span>
                        {dealers.address.postalCode}&nbsp;
                        {dealers.address.city}
                      </span>
                      <span> {dealers.address.country}</span>
                    </Grid>
                  ) : (
                    <Grid item md={3} className={styles.divAddress}>
                      <span>{dealers.address.street} </span>
                      <span> {dealers.address.city}</span>
                      <span>
                        {" "}
                        {dealers.address.country},
                        {dealers.address.postalCode}
                      </span>
                    </Grid>
                  )
                }
              />
              <>
                {editMode ? (
                  <Grid item md={3}>
                    <Box pr={2}>
                      <TextValidator
                        value={emailText}
                        onChange={emailOnChange}
                        name={"emailInput_" + dealers.completeDealerId}
                        id={"emailInput_" + dealers.completeDealerId}
                        validators={["required", "isEmail"]}
                        errorMessages={[
                          t("Error.requiredField"),
                          t("Error.invalidInput"),
                        ]}
                      />
                    </Box>
                  </Grid>
                ) : (
                  <Grid item md={3} className={styles.divEmail}>
                    {emailText ? (
                      <span className={styles.emailText}>{emailText}</span>
                    ) : (
                      t("DealerShipDetails.emailAddress")
                    )}
                  </Grid>
                )}
                {editMode ? (
                  <Grid item md={3}>
                    <Box pr={2}>
                      <TextValidator
                        onChange={phoneOnChange}
                        value={phoneText}
                        name={"phoneInput_" + dealers.completeDealerId}
                        id={"phoneInput_" + dealers.completeDealerId}
                        validators={["required"]}
                        errorMessages={[t("Error.requiredField")]}
                      />
                    </Box>
                  </Grid>
                ) : (
                  <Grid
                    item
                    md={3}
                    className={`${styles.divPhone} editPhoneOn`}
                  >
                    {phoneText ? (
                      <span className={styles.phoneText}>{phoneText}</span>
                    ) : (
                      t("DealerShipDetails.phoneNumber")
                    )}
                  </Grid>
                )}
                <Grid
                  item
                  md={3}
                  className={
                    showAddress
                      ? styles.contactEditDiv
                      : styles.contactEditDivNoAddress
                  }
                >
                  {editMode ? (
                    <>
                      <EMPButton
                        testId={"confirmInfo_" + dealers.regionalId}
                        submitButton
                        disabled={
                          UpperCaseText(user.userType) === "FIELD_VIEW"
                        }
                        variant="text"
                      >
                        {t("BusinessSettings.save")}
                      </EMPButton>

                      <EMPButton
                        testId={"cancelInfo_" + dealers.regionalId}
                        variant="text"
                        onClick={cancelEdit}
                      >
                        {t("BusinessSettings.cancelButton")}
                      </EMPButton>
                    </>
                  ) : (
                    <HasPermissionTo
                      perform={["dealershipDetails:write"]}
                      permissions={rolePermissions}
                      condition={isViewingMainSDLR === false}
                      render={() => (
                        <Button
                          id={`Edit_${dealers.regionalId}`}
                          onClick={editDealershipInfo}
                        >
                          <EditIcon />
                        </Button>
                      )}
                    />
                  )}
                </Grid>
              </>
            </Grid>
          </ValidatorForm>
        </div>
        <DealerEmployeeTable {...props} />
      </Accordion>
    </div>
  );
};

DealerContactInfo.propTypes = {
  dealers: PropTypes.shape({
    emailAddress: PropTypes.string,
    phoneNumber: PropTypes.string,
    name: PropTypes.string,
    regionalId: PropTypes.string,
    completeDealerId: PropTypes.string,
    address: PropTypes.shape({
      street: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
      postalCode: PropTypes.string,
    }),
  }).isRequired,
  user: PropTypes.shape({
    market: PropTypes.string,
    userType: PropTypes.string,
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.object,
    }),
  }).isRequired,
  updateDealerPhoneAndEmail: PropTypes.func.isRequired,
  isMainViewingSDLR: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation("emp")(DealerContactInfo);
